// @flow

import { ApprovableRevisionEnum } from "@modules/service/constants";
import type { State } from "@store/reducers";
import type { ApprovableContainer, HierarchyAssignment } from "@modules/service/types";
import type {
  EapRevision,
  EapMainDetails,
  EapMainDetailsDiff,
  EapReasonSpecificUsageDiff,
} from "@modules/service/reducer/eap-signposting/types";
import { validateHierarchyAssignmentSchema } from "./index";

const jsondiffpatch = require("jsondiffpatch");

export const getEapList = (state: State): Array<ApprovableContainer<EapRevision>> =>
  state.service.eapSignposting.list || [];

export const getEapActiveList = (state: State): Array<EapRevision> => {
  return getEapList(state)
    .filter(c => c.approvalState.indexOf("ACTIVE_") !== -1)
    .reduce(
      (acc, c) =>
        c.active
          ? [
              ...acc,
              {
                ...c.active,
                hierarchyAssignments: c.hierarchyAssignments,
              },
            ]
          : acc,
      [],
    );
};

export const getEapPendingList = (state: State): Array<EapRevision> => {
  return getEapList(state)
    .filter(c => c.approvalState.indexOf("PENDING_REVIEW") !== -1)
    .reduce(
      (acc, c) =>
        c.draft
          ? [
              ...acc,
              {
                ...c.draft,
                hierarchyAssignments: c.hierarchyAssignments,
              },
            ]
          : acc,
      [],
    );
};

export const getEapDraftList = (state: State): Array<EapRevision> => {
  return getEapList(state)
    .filter(c => c.approvalState === "DRAFT_ONLY" || c.approvalState === "ACTIVE_WITH_DRAFT")
    .reduce(
      (acc, c) =>
        c.draft
          ? [
              ...acc,
              {
                ...c.draft,
                hierarchyAssignments: c.hierarchyAssignments,
              },
            ]
          : acc,
      [],
    );
};

export const getEapContainer = (state: State): ?ApprovableContainer<EapRevision> =>
  state.service.eapSignposting.current;

export const getEapAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.eapSignposting.assignments;

export const getEapDraftAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.eapSignposting.draftAssignments;

export const getEapDraft = (state: State): ?EapRevision => state.service.eapSignposting.draft;

export const isEapDraftAssignmentsValid = (state: State): boolean => {
  const assignments = getEapDraftAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

export const isEapAssignmentsValid = (state: State): boolean => {
  const assignments = getEapAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

export const getEapMainDetails = (state: State, status: string): ?EapMainDetails => {
  const eap = getEapContainer(state);
  const eapRevision = eap ? eap[status] : null;
  const schema = getEapMainDetailsSchema(state);

  if (!eapRevision || !schema) {
    return null;
  }

  return {
    name: eapRevision.name,
    phoneNumber: eapRevision.phoneNumber,
    usageInstructions: eapRevision.usageInstructions,
    activeFrom: eapRevision.activeFrom,
    activeTo: eapRevision.activeTo,
    isActive: eapRevision.isActive,
  };
};

export type GetEapMainDetailsDiff = (state: State) => EapMainDetailsDiff;
export const getEapMainDetailsDiff: GetEapMainDetailsDiff = (state: State) => {
  const draft = getEapMainDetails(state, "draft");
  const active = getEapMainDetails(state, ApprovableRevisionEnum.ACTIVE);

  return {
    draft,
    active,
    deltas: draft && active ? jsondiffpatch.diff(draft, active) : null,
  };
};

export const getEapReasonSpecificUsage = (state: State, status: string) => {
  const eap = getEapContainer(state);
  const eapRevision = eap ? eap[status] : null;

  if (!eapRevision) {
    return null;
  }

  return eapRevision.reasonSpecificUsages;
};

export const getEapReasonSpecificUsageDiff = (state: State): EapReasonSpecificUsageDiff => {
  const draft = getEapReasonSpecificUsage(state, "draft");
  const active = getEapReasonSpecificUsage(state, ApprovableRevisionEnum.ACTIVE);

  return {
    draft,
    active,
    deltas: draft && active ? jsondiffpatch.diff(draft, active) : null,
  };
};

// EAP SCHEMA

export const getEapSchema = (state: State): ?Object => (state.service.eapSignposting || {}).schema;

export const getEapMainDetailsSchema = (state: State): Object | null => {
  const eapSchema1 = getEapSchema({ ...state });
  if (eapSchema1 && eapSchema1.properties) {
    // Cloning the object in order to delete safetly the reasonSpecificUsages prop
    const eapSchema = JSON.parse(JSON.stringify(eapSchema1));
    delete eapSchema.properties.reasonSpecificUsages;
    return eapSchema;
  }
  return null;
};

export const getEapReasonSpecificUsageSchema = (state: State): Object | null => {
  const eapSchema = getEapSchema(state);
  return eapSchema && eapSchema.properties ? eapSchema.properties.reasonSpecificUsages : null;
};

// EAP MODALS

export const getShowAddModal = (state: State): boolean =>
  state.service.eapSignposting.modals.showAddModal;

export const getShowEditModal = (state: State): boolean =>
  state.service.eapSignposting.modals.showEditModal;

export const getShowEditAssignmentsModal = (state: State): boolean =>
  state.service.eapSignposting.modals.showEditAssignmentsModal;

export const getModalStep = (state: State): number => state.service.eapSignposting.modals.modalStep;

export const getShowDiscardModal = (state: State): boolean =>
  state.service.eapSignposting.modals.showDiscardModal;

export const getShowSubmitModal = (state: State): boolean =>
  state.service.eapSignposting.modals.showSubmitModal;

export const getShowCancelModal = (state: State): boolean =>
  state.service.eapSignposting.modals.showCancelModal;

export const getShowCancelDiscardModal = (state: State): boolean =>
  state.service.eapSignposting.modals.showCancelDiscardModal;

export const getShowDisableModal = (state: State): boolean =>
  state.service.eapSignposting.modals.showDisableModal;
